import clsx from 'clsx';
import Button from 'components/atoms/Button/Button';
import { Checkbox } from 'components/atoms/Checkbox/Checkbox';
import LocaleAwareImage from 'components/atoms/LocaleAwareImage/LocaleAwareImage';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import PasswordInput, {
  PASSWORD_REQUIREMENT_DESCRIPTIONS,
} from 'components/atoms/TextInput/PasswordInput';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
import { usePasswordValidator } from 'hooks/usePasswordValidator';
import React from 'react';
import { MdInfoOutline, MdOutlineCheckCircle } from 'react-icons/md';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { PasswordStep_termsQuery } from './__generated__/PasswordStep_termsQuery.graphql';
import { StepProps } from './RegistrationForm';
import classes from './RegistrationForm.module.scss';
import useFrontEndEventTrigger from 'hooks/useFrontEndEventTrigger';

const termsQuery = graphql`
  query PasswordStep_termsQuery {
    termsAndConditions {
      version
      formatted
    }
  }
`;

export function PasswordStep({
  onNext,
  onPrevious,
  progressIndicator,
  currentFormData,
  menu,
  error,
}: StepProps) {
  const [passwordIsValid, setPassword] = usePasswordValidator();
  const isSocial = Boolean(currentFormData.provider || currentFormData.socialToken);
  const { frontEndEventTrigger } = useFrontEndEventTrigger();

  const { termsAndConditions } = useLazyLoadQuery<PasswordStep_termsQuery>(termsQuery, {});

  function handleSubmit(formData: FormData) {
    onNext?.({
      password: formData.get('password')?.toString() ?? '',
      terms: formData.get('privacyPolicy') === 'on',
      termsVersion: termsAndConditions?.version ?? '1.0',
    });
    frontEndEventTrigger('Registration step', 'Password');
  }

  function handlePrevious(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    onPrevious?.();
  }

  return (
    <div className={classes.registrationFormContainer}>
      <RegistrationCard
        progressIndicator={progressIndicator}
        menu={menu}
        title={isSocial ? 'Terms & Conditions' : 'Create a password'}
        additionalInfo={{
          title: 'Surveys tailored to you',
          subtitle: (
            <>
              <Txt component={'p'}>
                Once you sign up, answer a few questions about yourself and start receiving surveys
                that match your interests. The more time you spend with LifePoints, the bigger the
                rewards.
              </Txt>
              <Txt component={'p'}>Start earning today!</Txt>
            </>
          ),
          image: (
            <LocaleAwareImage
              alt=""
              src="/assets/registration-password.png"
              width={431}
              height={234}
            />
          ),
        }}
      >
        {error}
        {!isSocial && <Txt>Create a secure and memorable password.</Txt>}
        <Form
          submitDisabled={!(passwordIsValid || isSocial)}
          submitName="Sign Up"
          onSubmit={handleSubmit}
          buttonWrapperClassName={classes.buttonWrapper}
          submitButtonClassName={classes.button}
          submitButtonType="primary"
          extraActionButtons={
            <Button
              buttonSize="large"
              buttonType="text"
              className={clsx(classes.button)}
              onClick={handlePrevious}
            >
              <Txt>Back</Txt>
            </Button>
          }
        >
          {!isSocial && (
            <>
              <PasswordInput
                id="password"
                title="Password"
                placeholder="Enter password"
                required
                autoComplete="new-password"
                minLength={8}
                onChange={(e) => {
                  setPassword(e.currentTarget.value);
                }}
                overrideErrorMessage={(_validity, _value) => {
                  return undefined;
                }}
              />
              <Txt component="p" className={clsx(classes.passwordValidation)} skipTranslation>
                {passwordIsValid ? (
                  <MdOutlineCheckCircle
                    className={clsx(classes.passwordIcon, classes.passwordIconValid)}
                  />
                ) : (
                  <MdInfoOutline
                    className={clsx(classes.passwordIcon, classes.passwordIconInvalid)}
                  />
                )}
                <Txt>{PASSWORD_REQUIREMENT_DESCRIPTIONS}</Txt>
              </Txt>
            </>
          )}
          <Checkbox
            labelElement={
              <Txt asMarkdown skipTranslation={true}>
                {termsAndConditions?.formatted}
              </Txt>
            }
            name="privacyPolicy"
            required
            defaultChecked={error ? false : currentFormData.terms}
            className={classes.checkboxOverride}
            skipTranslation={true}
          />
          <Txt component={'p'} className={classes.terms}>
            I acknowledge that by using this site, I agree to be contacted by Lightspeed and
            LifePoints about updates, services, and offers. We promise no third parties will contact
            you.
          </Txt>
          <Txt component={'p'} className={classes.terms}>
            LifePoints is owned and operated by Lightspeed Research Limited, a Kantar Group company.
          </Txt>
        </Form>
      </RegistrationCard>
    </div>
  );
}
